import { api } from './api';

// Types
export interface Performance {
    views: number;
    engagementRate: number;
    clicks: number;
    estimatedRevenue: number;
}

export interface ScheduledPost {
    id: string;
    _id: string;
    title: string;
    description?: string;
    contentItemId?: string;
    teamId: string;
    platformConnectionId: string | PlatformConnection;
    campaignId?: string | Campaign;
    scheduledFor: string;
    status: 'draft' | 'scheduled' | 'ready' | 'needs_media' | 'publishing' | 'published' | 'failed' | 'cancelled';
    platformPostId?: string;
    errorMessage?: string;
    performance: Performance;
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface Campaign {
    id: string;
    _id: string;
    name: string;
    description?: string;
    teamId: string;
    platformIds: string[] | PlatformConnection[];
    scheduledPosts: string[] | ScheduledPost[];
    status: 'draft' | 'active' | 'completed' | 'cancelled';
    startDate?: string;
    endDate?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PlatformConnection {
    id: string;
    _id: string;
    teamId: string;
    platform: 'youtube' | 'instagram' | 'tiktok' | 'twitter' | 'linkedin' | 'facebook' | 'blog';
    accountName: string;
    accountId?: string;
    status: 'connected' | 'auth_expired' | 'disconnected' | 'error';
    lastSync?: string;
    apiQuotaUsed?: number;
    apiQuotaLimit?: number;
    postsToday?: number;
    postsLimit?: number;
    createdAt: string;
    updatedAt: string;
}

export interface PublishingStats {
    scheduled: number;
    published: number;
    failed: number;
    total: number;
}

// API Response Types
interface ApiResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Service
export const publishingService = {
    // Stats
    async getStats(): Promise<PublishingStats> {
        const response = await api.get<ApiResponse<PublishingStats>>('/publishing/stats');
        return response.data.data;
    },

    // Scheduled Posts
    async getPosts(filters?: {
        campaignId?: string;
        platformConnectionId?: string;
        status?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<ScheduledPost[]> {
        const params = new URLSearchParams();
        if (filters?.campaignId) params.append('campaignId', filters.campaignId);
        if (filters?.platformConnectionId) params.append('platformConnectionId', filters.platformConnectionId);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.startDate) params.append('startDate', filters.startDate);
        if (filters?.endDate) params.append('endDate', filters.endDate);

        const response = await api.get<ApiResponse<ScheduledPost[]>>(`/publishing/posts?${params.toString()}`);
        return response.data.data.map((post: ScheduledPost) => ({ ...post, id: post._id }));
    },

    async getPost(id: string): Promise<ScheduledPost> {
        const response = await api.get<ApiResponse<ScheduledPost>>(`/publishing/posts/${id}`);
        return { ...response.data.data, id: response.data.data._id };
    },

    async createPost(data: {
        title: string;
        description?: string;
        contentItemId?: string;
        platformConnectionId: string;
        campaignId?: string;
        scheduledFor: string;
        status?: string;
    }): Promise<ScheduledPost> {
        const response = await api.post<ApiResponse<ScheduledPost>>('/publishing/posts', data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async updatePost(id: string, data: Partial<ScheduledPost>): Promise<ScheduledPost> {
        const response = await api.patch<ApiResponse<ScheduledPost>>(`/publishing/posts/${id}`, data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async deletePost(id: string): Promise<void> {
        await api.delete(`/publishing/posts/${id}`);
    },

    async publishNow(id: string): Promise<ScheduledPost> {
        const response = await api.post<ApiResponse<ScheduledPost>>(`/publishing/posts/${id}/publish-now`);
        return { ...response.data.data, id: response.data.data._id };
    },

    // Campaigns
    async getCampaigns(filters?: { status?: string }): Promise<Campaign[]> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);

        const response = await api.get<ApiResponse<Campaign[]>>(`/publishing/campaigns?${params.toString()}`);
        return response.data.data.map((campaign: Campaign) => ({ ...campaign, id: campaign._id }));
    },

    async getCampaign(id: string): Promise<Campaign> {
        const response = await api.get<ApiResponse<Campaign>>(`/publishing/campaigns/${id}`);
        return { ...response.data.data, id: response.data.data._id };
    },

    async createCampaign(data: {
        name: string;
        description?: string;
        platformIds?: string[];
        status?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<Campaign> {
        const response = await api.post<ApiResponse<Campaign>>('/publishing/campaigns', data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async updateCampaign(id: string, data: Partial<Campaign>): Promise<Campaign> {
        const response = await api.patch<ApiResponse<Campaign>>(`/publishing/campaigns/${id}`, data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async deleteCampaign(id: string): Promise<void> {
        await api.delete(`/publishing/campaigns/${id}`);
    },

    // Platform Connections
    async getPlatforms(): Promise<PlatformConnection[]> {
        const response = await api.get<ApiResponse<PlatformConnection[]>>('/publishing/platforms');
        return response.data.data.map((platform: PlatformConnection) => ({ ...platform, id: platform._id }));
    },

    async getPlatform(id: string): Promise<PlatformConnection> {
        const response = await api.get<ApiResponse<PlatformConnection>>(`/publishing/platforms/${id}`);
        return { ...response.data.data, id: response.data.data._id };
    },

    async connectPlatform(data: {
        platform: string;
        accountName: string;
        accountId?: string;
    }): Promise<PlatformConnection> {
        const response = await api.post<ApiResponse<PlatformConnection>>('/publishing/platforms/connect', data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async updatePlatform(id: string, data: Partial<PlatformConnection>): Promise<PlatformConnection> {
        const response = await api.patch<ApiResponse<PlatformConnection>>(`/publishing/platforms/${id}`, data);
        return { ...response.data.data, id: response.data.data._id };
    },

    async refreshPlatform(id: string): Promise<PlatformConnection> {
        const response = await api.post<ApiResponse<PlatformConnection>>(`/publishing/platforms/${id}/refresh`);
        return { ...response.data.data, id: response.data.data._id };
    },

    async disconnectPlatform(id: string): Promise<void> {
        await api.delete(`/publishing/platforms/${id}`);
    },
};

export default publishingService;
